import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, fetchSignInMethodsForEmail, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { formatAuthError } from '../utils/authErrors';
import '../styles.css';

const quotes = [
  { text: 'Tires, Cole, is what wins a race.', author: 'Harry Hogge (Days of Thunder)' },
  { text: 'To race is to live, all the rest is simply waiting', author: 'Steve McQueen' },
  { text: "If you're in control, you're not going fast enough.", author: 'Parnelli Jones' },
  { text: 'Straight roads are for fast cars, turns are for fast drivers.', author: 'Colin McRae' },
  { text: 'If you no longer go for a gap that exists, you are no longer a racing driver', author: 'Ayrton Senna' },
  { text: "What's behind you doesn't matter", author: 'Enzo Ferrari' },
  { text: "It's marvelous to go very fast", author: 'Lisa (Grand Prix 1966)' },
  { text: "Just leave me alone, I know what to do", author: 'Kimi Räikkönen' },
  { text: "Auto racing, bull fighting, and mountain climbing are the only real sports… all the others are games.", author: 'Ernest Hemingway' },
  { text: "Faster, faster, faster, until the thrill of speed overcomes the fear of death.", author: 'Hunter Thompson' },
  { text: "Auto racing began five minutes after the second car was built.", author: 'Henry Ford' },
  { text: "To achieve anything in this game you must be prepare to dabble in the boundary of disaster.", author: 'Sterling Moss' },
];

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [randomQuote, setRandomQuote] = useState({ text: '', author: '' });
  const [signInMethods, setSignInMethods] = useState([]);

  useEffect(() => {
    const quote = quotes[Math.floor(Math.random() * quotes.length)];
    setRandomQuote(quote);
  }, []);

  const fetchMethods = async (email) => {
    if (!email || !email.includes('@')) return [];
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      setSignInMethods(methods);
      return methods;
    } catch (err) {
      setSignInMethods([]);
      return [];
    }
  };

  const handleLogin = async () => {
    setError(null);
    let methods = signInMethods;
    if (!methods.length && email) {
      methods = await fetchMethods(email);
    }
    if (methods.includes('google.com') && !methods.includes('password')) {
      setError("This email is linked to Google. Please sign in with Google.");
      return;
    }
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      if (err.code === 'auth/invalid-credential' && methods.includes('google.com')) {
        setError("This email is linked to Google. Please sign in with Google.");
      } else if (err.code === 'auth/user-not-found' && methods.length === 0) {
        setError("No account found with this email. Try signing in with Google or register a new account.");
      } else if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError(
          <span>
            Invalid credentials. <button onClick={handlePasswordReset} className="btn-link">Forgot password?</button>
          </span>
        );
      } else {
        setError(formatAuthError(err));
      }
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      alert('Password reset email sent. Please check your email and follow the instructions.');
    } catch (err) {
      setError(formatAuthError(err));
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err) {
      setError(formatAuthError(err));
    }
  };

  return (
    <div className="login-container">
      <img src="/logo.png" alt="Logo" className="login-logo" />
      <div className="login-content">
        <h1 className="login-title">Hot Setup</h1>
        <p className="login-quote" data-author={randomQuote.author}>{randomQuote.text}</p>
        {error && <p className="login-error">{error}</p>}
        <input
          type="email"
          value={email}
          onChange={e => {
            setEmail(e.target.value);
            setSignInMethods([]);
          }}
          onBlur={() => fetchMethods(email)}
          placeholder="Email"
          className="login-input"
        />
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Password"
          className="login-input"
        />
        <button onClick={handleLogin} className="btn btn-save">
          Login
        </button>
        <button onClick={handleGoogleSignIn} className="btn btn-google" style={{
          backgroundColor: '#fff',
          color: '#333',
          border: '1px solid #ddd',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          fontWeight: '500'
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>
        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: '#666' }}>
          Don't have an account? <a href="/register" style={{ color: '#2563eb', textDecoration: 'none' }}>Start Free Trial</a>
        </p>
      </div>
    </div>
  );
}

export default Login;